import { useState, useEffect, useCallback } from "react";
import axios, { AxiosResponse } from "axios";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";

// Define the profile structure based on what your API returns
interface Profile {
  id: number;
  email: string;
  username: string;
  // Add other fields as per your API response
  permissions?: string[];
}

// Define the function to fetch the profile
async function fetchProfile(
  accessToken: string,
  apiUrl: string
): Promise<AxiosResponse<Profile>> {
  const headers = {
    Token: `${accessToken}`,
  };
  return axios.get(`${apiUrl}/auth/profile`, { headers });
}

function useProfile() {
  const { hasAuthenticated, logout } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const apiUrl = process.env.REACT_APP_API_URL || ""; // Default empty string fallback
  const navigate = useNavigate();

  // Handle token expiration by logging out the user and navigating to the login page
  const handleTokenExpiration = useCallback(() => {
    logout();
    navigate("/");
    // toastMessage({
    //   content: "Token expired",
    // });
  }, [logout, navigate]);

  // Check the token and fetch the profile if valid
  const checkTokenExpiration = useCallback(() => {
    if (hasAuthenticated) {
      setLoading(true);
      fetchProfile(hasAuthenticated, apiUrl)
        .then((response) => {
          setProfile(response.data);
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          if (axios.isAxiosError(err) && err.response?.status === 401) {
            handleTokenExpiration();
          }
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAuthenticated, apiUrl]);

  // useEffect to trigger the profile fetch when the user is authenticated
  useEffect(() => {
    if (hasAuthenticated) {
      checkTokenExpiration();

      // Set up an interval to re-check token every day (1 day = 86400000 ms)
      const intervalId = setInterval(checkTokenExpiration, 86400000);

      return () => clearInterval(intervalId);
    }
  }, [hasAuthenticated, checkTokenExpiration]);

  return {
    profile,
    loading,
  };
}

export default useProfile;
